import { ReactNode, isValidElement, useMemo } from 'react'

import { useControllableValue, useDebounceFn } from 'ahooks'
import { Select, SelectProps } from 'antd'
import { isBoolean, isNil, isNumber, isString } from 'lodash'

import { deduplicate, toArray } from '../../utils/array'
import { isScrollEnd } from '../../utils/dom'
import { isEmpty } from '../../utils/object'
import { getReactNodeText } from '../../utils/react'
import { DataEmpty } from '../DataEmpty'
import { DataError } from '../DataError'

import { RemoteSelectProps, SelectMode } from './interface'
import { useRemoteSelectRequest } from './useRemoteSelectRequset'

const contains = (text: string, keyword: string) => {
  return text.toLowerCase().includes(keyword.toLowerCase())
}

const filterOption = (input: string, option: { label?: ReactNode } = {}) => {
  const label = option.label

  if (isNil(label)) {
    return false
  }

  if (isString(label) || isNumber(label) || isBoolean(label)) {
    return contains(label.toString(), input)
  }

  if (isValidElement(label)) {
    const text = getReactNodeText(label)
    return contains(text, input)
  }

  return false
}

export const RemoteSelect = <T, P, M extends SelectMode | undefined>({
  ref,
  /* -------------------------------------------------------------------------- */
  /*                              useRequest Props                              */
  /* -------------------------------------------------------------------------- */
  api,
  readyRequest,
  requestParams,
  refreshDeps,
  onRequestSuccess,
  /* -------------------------------------------------------------------------- */
  /*                                Select Props                                */
  /* -------------------------------------------------------------------------- */
  className,
  rootClassName,
  style,
  mode,
  allowClear,
  disabled,
  placeholder,
  popupRender,
  optionRender,
  labelRender,

  /* -------------------------------------------------------------------------- */
  /*                             RemoteSelect Props                             */
  /* -------------------------------------------------------------------------- */
  getOptionValue,
  resolveOptions,
  // useControllableValue 是根据 hasOwnProperty 来判断是否受控，所以 value 跟 onChange 不能解构出来
  ...restProps
}: RemoteSelectProps<T, P, M>) => {
  const {
    data,
    error,
    loading,
    shouldSearch,
    refreshAsync,
    fetchNextPage,
    fetchSearch
  } = useRemoteSelectRequest({
    api,
    readyRequest,
    requestParams,
    refreshDeps,
    onRequestSuccess
  })

  const { run: debounceFetchSearch } = useDebounceFn(fetchSearch, {
    wait: 500,
    trailing: true
  })

  const [value, setValue] = useControllableValue(restProps)

  // 分页时 value 数据可能并不存在于 data 中，合并一下
  const allData = useMemo(() => {
    const allData = isEmpty(value) ? data : data.concat(toArray(value))
    const uniqueData = deduplicate(
      allData,
      (value1, value2) => getOptionValue(value1) === getOptionValue(value2)
    ).filter(Boolean)
    return uniqueData
  }, [value, data, getOptionValue])

  const options = useMemo(
    () => resolveOptions(allData),
    [allData, resolveOptions]
  )

  const isMultiple = mode === 'multiple' || mode === 'tags'

  const handleChange = (keys: string | string[]) => {
    const values = toArray(keys).map(key => {
      return allData.find(item => getOptionValue(item) === key)
    })
    setValue(isMultiple ? values : values[0])
  }

  const onPopupScroll: SelectProps['onPopupScroll'] = e => {
    // 到底部要重新发起请求获取下一页的数据
    if (isScrollEnd(e.target as HTMLElement)) {
      fetchNextPage()
    }
  }

  const selectedValue = useMemo(
    () =>
      Array.isArray(value)
        ? value.map(getOptionValue)
        : isEmpty(value)
          ? undefined
          : getOptionValue(value),
    [getOptionValue, value]
  )
  return error ? (
    <DataError error={error} loading={loading} reload={refreshAsync} />
  ) : (
    <Select
      ref={ref}
      allowClear={allowClear}
      className={className}
      disabled={disabled}
      labelInValue={false}
      labelRender={labelRender}
      loading={loading}
      mode={mode}
      notFoundContent={<DataEmpty loading={loading} reload={refreshAsync} />}
      optionLabelProp='label'
      optionRender={optionRender}
      options={options}
      placeholder={placeholder}
      popupRender={popupRender}
      rootClassName={rootClassName}
      style={{ width: '100%', ...style }}
      value={selectedValue}
      showSearch={{
        optionFilterProp: 'label',
        filterOption: shouldSearch ? false : filterOption,
        onSearch: shouldSearch ? debounceFetchSearch : undefined
      }}
      onChange={handleChange}
      onPopupScroll={onPopupScroll}
    />
  )
}
